import { hasArtificeSessionMarker } from '../auth/artificeContinue';
import { getStoredWebAuth, setStoredWebAuth } from '../miniapp/api/miniappApi';
import { artificeFetch } from '../api/artificeHttp';
import {
  getSessionAccessGeneration,
  readSessionAccessToken,
  rememberSessionAccessToken,
} from './sessionAccessToken';

export {
  clearSessionAccessToken,
  getSessionAccessGeneration,
  readLiveAccessToken,
  readSessionAccessToken,
  rememberSessionAccessToken,
  withSessionAccessHeaders,
} from './sessionAccessToken';

export const TERMINAL_AUTH_RESOLVED_EVENT = 'jester:terminal-auth-resolved';

export type TerminalSessionRefreshJson = {
  success?: boolean;
  token?: string;
  userId?: string | number | null;
  telegramId?: string | number | null;
  error?: string;
  message?: string;
  loginUsername?: string | null;
  hasPasswordLogin?: boolean;
  authMethod?: 'telegram' | 'wallet' | 'password';
  requiresHyperliquidOnboarding?: boolean;
  hasHyperliquidKey?: boolean;
  /** Live extra-agent check — false when the API agent is expired, revoked, or missing. */
  hyperliquidKeyActive?: boolean;
  hyperliquidKeyIsActive?: boolean;
  isHyperliquidKeyActive?: boolean;
  requiresApiKeyRefresh?: boolean;
  apiKeyStatus?: string;
  apiKeyRefreshMessage?: string;
  hyperliquidAccountMismatch?: boolean;
  walletAddress?: string | null;
};

export type TerminalSessionRefreshFetch = {
  ok: boolean;
  data: TerminalSessionRefreshJson;
  /** HTTP status, or 0 when the request never reached the server. */
  status: number;
};

let refreshFetchInFlight: Promise<TerminalSessionRefreshFetch> | null = null;
/** Set for the rest of the tab after Sign out until the next explicit sign-in. */
let refreshBlocked = false;
/**
 * Terminal-style session recovery (#29 family): after a **cold** anonymous refresh
 * failure (401 / no session, no device hints), short-circuit further POST
 * `/api/terminal/session/refresh` for this tab — same idea as `refreshBlocked`
 * after sign-out and `hadClientSessionHintBeforeExpiry` for expired boots.
 */
let suppressAnonymousSessionRefresh = false;

export function clearAnonymousSessionRefreshSuppression(): void {
  suppressAnonymousSessionRefresh = false;
}

/** True when this tab has already proven there is no restorable terminal session. */
export function isAnonymousSessionRefreshSuppressed(): boolean {
  return shouldSkipAnonymousSessionRefresh();
}

function shouldSkipAnonymousSessionRefresh(): boolean {
  if (!suppressAnonymousSessionRefresh) return false;
  if (hasTerminalSessionHint() || hasArtificeSessionMarker()) {
    suppressAnonymousSessionRefresh = false;
    return false;
  }
  return true;
}

function noteAnonymousSessionRefreshFailure(status: number, data: TerminalSessionRefreshJson): void {
  const noSession =
    status === 401 ||
    (status === 200 && data?.success !== true && !data?.token);
  if (!noSession) return;
  if (hasTerminalSessionHint() || hasArtificeSessionMarker()) return;
  suppressAnonymousSessionRefresh = true;
}
/** Pause refresh after 429 so a dead session cannot fill the IdP budget. */
let refreshCooldownUntil = 0;

export function setTerminalSessionRefreshBlocked(blocked: boolean): void {
  refreshBlocked = blocked;
  if (blocked) refreshCooldownUntil = 0;
}

export function isTerminalSessionRefreshBlocked(): boolean {
  return refreshBlocked;
}

function refreshCooldownMs(data: TerminalSessionRefreshJson, status: number): number {
  if (status !== 429) return 0;
  const retry = Number((data as { retryAfter?: unknown }).retryAfter);
  if (Number.isFinite(retry) && retry > 0) return Math.min(Math.ceil(retry) * 1000, 5 * 60 * 1000);
  return 30_000;
}

/** Await the shared refresh POST so logout can run after it (and discard its token). */
export async function waitForTerminalSessionRefreshIdle(): Promise<void> {
  const pending = refreshFetchInFlight;
  if (pending) await pending.catch(() => undefined);
}

const SIGNED_OUT_REFRESH: TerminalSessionRefreshFetch = {
  ok: false,
  data: { success: false, error: 'Signed out' },
  status: 401,
};

/** Single in-flight POST /session/refresh so Oroboros + Mini App + useAuth cannot rotate the same cookie twice. */
export function fetchTerminalSessionRefresh(): Promise<TerminalSessionRefreshFetch> {
  if (refreshBlocked) return Promise.resolve(SIGNED_OUT_REFRESH);
  if (shouldSkipAnonymousSessionRefresh()) return Promise.resolve(SIGNED_OUT_REFRESH);
  if (Date.now() < refreshCooldownUntil) {
    return Promise.resolve({
      ok: false,
      data: { success: false, error: 'Too many requests. Please try again in 5 minutes.' },
      status: 429,
    });
  }
  if (refreshFetchInFlight) return refreshFetchInFlight;
  const generation = getSessionAccessGeneration();
  const pending = (async (): Promise<TerminalSessionRefreshFetch> => {
    try {
      if (refreshBlocked) return SIGNED_OUT_REFRESH;
      const resp = await artificeFetch('/api/terminal/session/refresh', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'Artifice',
          Accept: 'application/json',
        },
      });
      const data = (await resp.json().catch(() => ({}))) as TerminalSessionRefreshJson;
      if (resp.status === 429) {
        refreshCooldownUntil = Date.now() + refreshCooldownMs(data, resp.status);
      }
      if (resp.ok && data?.success && data?.token) {
        suppressAnonymousSessionRefresh = false;
        rememberSessionAccessToken(data.token, generation);
      } else {
        noteAnonymousSessionRefreshFailure(resp.status, data);
      }
      // Do not clear the in-tab JWT when refresh cookies miss. Password login
      // stores that JWT first; wiping it here made every follow-up API 401.
      return { ok: resp.ok, data, status: resp.status };
    } catch {
      return { ok: false, data: {}, status: 0 };
    }
  })().finally(() => {
    if (refreshFetchInFlight === pending) refreshFetchInFlight = null;
  });
  refreshFetchInFlight = pending;
  return pending;
}

let refreshInFlight: Promise<string | null> | null = null;

/** Deduped silent session refresh — sets httpOnly cookie. Does not mirror JWT into JS (M1.1). */
export async function performTerminalSessionRefresh(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const { ok, data: js } = await fetchTerminalSessionRefresh();
      if (ok && js?.success && js?.token) {
        (window as any).JesterApp = (window as any).JesterApp || {};
        // Cookie session is authoritative — clear any legacy XSS-readable mirror.
        (window as any).JesterApp.authToken = undefined;
        const wallet = String(js.walletAddress || '').trim();
        if (/^0x[a-fA-F0-9]{40}$/.test(wallet)) {
          const lower = wallet.toLowerCase();
          (window as any).JesterApp.userWallet = lower;
          try {
            localStorage.setItem('jester_user_wallet', lower);
          } catch {}
        }
        setStoredWebAuth();
        return js.token as string;
      }
      return null;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export function hasTelegramInitData(): boolean {
  try {
    return !!String((window as any)?.Telegram?.WebApp?.initData || '').trim();
  } catch {
    return false;
  }
}

/** True when cookie/TG session likely exists (may still need refresh). */
export function hasTerminalSessionHint(): boolean {
  if (hasTelegramInitData()) return true;
  try {
    if (getStoredWebAuth()) return true;
    if (readSessionAccessToken()) return true;
    if (String((window as any)?.JesterApp?.authToken || '').trim()) return true;
  } catch {}
  return false;
}

export function dispatchTerminalAuthResolved(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(TERMINAL_AUTH_RESOLVED_EVENT));
  } catch {}
}
